import { isNil } from 'lodash';
import React, { useContext } from 'react';
import { StyleSheet } from 'react-native';
import { View } from '@shared/components/View';
import { MBClearButton } from '@shared/components/button';
import { MBIconV2, MBIconV2Name, MBImage } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2, MBTextOverlineBold } from '@shared/components/typography';
import {
  getComponentIcon,
  getComponentLabel,
  getComponentPlaceholder,
  isDisplayableComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { getTestId } from '@shared/util/getTestId';
import { useIsDesktop } from '@shared/util/hooks';
import classNames from './EditFieldContainer2.module.css';
import { EditFieldContainerProps } from './EditFieldContainerProps';

export function EditFieldContainer2({
  component,
  children,
  icon = getComponentIcon(component) || getFallbackComponentIcon(component),
  iconUrl,
  label = getComponentLabel(component),
  placeholder = getComponentPlaceholder(component),
  footer,
  subfooter,
  showIcon = true,
  showLabel = true,
  indentLabel = false,
  showPlaceholder = false,
  showClearButton = false,
  error,
  onPress,
  onLongPress,
  onClear,
  reserveIconSpace = false,
  testID = getTestId('card-field-cards', component.id),
  anchorName,
  compactTop = false,
  compactBottom = false,
  largerInput = true,
  isGrayScale = false,
  isItalic = false,
  style,
}: EditFieldContainerProps) {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const isDisplayable = isDisplayableComponent(component);
  const shouldHaveIcon = (showIcon || reserveIconSpace) && isDisplayable;
  const renderIconUrl = showIcon && shouldHaveIcon && !isNil(iconUrl);
  const renderIcon = showIcon && shouldHaveIcon && !isNil(icon) && !renderIconUrl;
  const renderLabel = showLabel && isDisplayable && !isNil(label);
  const renderPlaceholder = showPlaceholder && isDisplayable && !isNil(placeholder);
  const renderClearButton = showClearButton && !isNil(onClear);
  const hasError = !!error;

  const iconSize = isMobile && largerInput ? grid(4) : grid(3);
  const imageIconSize = isMobile && largerInput ? grid(3) : grid(2.5);

  return (
    <MBTouchableOpacity
      testID={testID}
      onPress={onPress}
      onLongPress={onLongPress}
      style={[
        styles.container,
        style,
        compactTop && styles.compactTop,
        compactBottom && styles.compactBottom,
      ]}
    >
      {renderLabel && (
        <MBTextOverlineBold
          color={hasError ? colors.error.border : colors.responsiveInput.label}
          style={[styles.label, indentLabel && styles.indentedLabel]}
        >
          {label}
        </MBTextOverlineBold>
      )}
      <View
        className={[
          classNames.wrapper,
          hasError && classNames.wrapperError,
          isGrayScale && classNames.wrapperGrayScale,
          isItalic && classNames.wrapperItalic,
        ]}
      >
        <View className={[classNames.row, isMobile && largerInput && classNames.rowPadded]}>
          {renderIcon && (
            <View className={classNames.icon}>
              <MBIconV2
                name={icon}
                size={iconSize}
                color={hasError ? colors.error.foreground : colors.accent}
              />
            </View>
          )}
          {renderIconUrl && (
            <View className={classNames.icon}>
              <MBImage
                source={{ uri: iconUrl }}
                style={{ width: imageIconSize, height: imageIconSize }}
              />
            </View>
          )}
          {reserveIconSpace && !renderIcon && !renderIconUrl && (
            <View className={classNames.icon} />
          )}
          <View className={classNames.content}>
            {renderPlaceholder && <View className={classNames.placeholder}>{placeholder}</View>}
            {children}
          </View>
          {renderClearButton && (
            <View className={classNames.clearButton}>
              <MBClearButton
                onPress={onClear}
                color={hasError ? colors.foregroundInactive : undefined}
              />
            </View>
          )}
        </View>
        {footer && <View className={classNames.footer}>{footer}</View>}
        <View style={{ anchorName }} className={classNames.anchor} />
      </View>
      {subfooter && <View className={classNames.subfooter}>{subfooter}</View>}
      {hasError && (
        <View className={classNames.error}>
          <MBTextBody2 color={colors.error.foreground}>{error}</MBTextBody2>
        </View>
      )}
    </MBTouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: grid(0.25),
    marginBottom: grid(0.25),
  },
  label: {
    marginLeft: grid(2),
    marginBottom: grid(0.75),
  },
  indentedLabel: {
    marginLeft: grid(2),
  },
  compactTop: {
    paddingTop: 0,
  },
  compactBottom: {
    paddingBottom: 0,
  },
});

function getFallbackComponentIcon(component: MBAPI.WorkflowTemplate_components): MBIconV2Name {
  if (component.id === 'name') {
    return 'title-t';
  }
  return 'squares-stacked';
}
